/**
 * Copy of the Revenue Tax panel (PIS/COFINS provisioned with the revenue, ADR 0024): the entity
 * fiscal settings panel. Spread into `tenantSettings` as the top-level `revenueTax` block.
 *
 * Setup errors, warnings and blockers live once in `@alinea/types` (`REVENUE_TAX_SETUP_MESSAGES`,
 * the class and slot labels). Assessment-persistence refusals are translated below by stable code
 * for future assessment surfaces; S2 adds no screen.
 */
export const revenueTaxPtBr = {
  revenueTax: {
    assessmentErrors: {
      TAX_ASSESSMENT_PURCHASE_CREDIT_OWNER_BOOK_MISMATCH:
        'Os livros do ativo, da provisão de origem e da apuração não estão conciliados. Revise esses vínculos antes de aprovar.',
      TAX_ASSESSMENT_COMMON_USE_DEPRECIATION_CORRECTION_DRIFT:
        'O histórico de depreciação mudou desde a preparação da correção. Atualize a apuração e revise os lançamentos e o período original antes de tentar novamente.',
      TAX_ASSESSMENT_COMMON_USE_ACCOUNTING_TREATMENT_REQUIRED:
        'O rateio do crédito do ativo altera seu custo. Na aprovação, informe o tratamento contábil e a evidência que sustenta essa decisão.',
      TAX_ASSESSMENT_COMMON_USE_PRIOR_PERIOD_CORRECTION_UNSUPPORTED:
        'A correção deste rateio exige revisão do período original e do histórico de depreciação. Preserve a classificação do erro e revise os períodos afetados antes de aprovar.',
      TAX_ASSESSMENT_ACCOUNTING_CORRECTION_EVIDENCE_REQUIRED:
        'Informe a referência e a descrição da evidência que sustenta o tratamento contábil escolhido.',
      TAX_ASSESSMENT_PRIOR_PERIOD_REOPEN_REQUIRED:
        'Para corrigir este erro, reabra o período contábil original e refaça a aprovação da apuração.',
      TAX_ASSESSMENT_PRIOR_AUTHORIZED_RESTATEMENT_REQUIRED:
        'Este erro afeta demonstrações já autorizadas e exige reapresentação comparativa. Esse fluxo ainda não está disponível; mantenha a apuração em revisão.',
      TAX_ASSESSMENT_FIXED_ASSET_COMMON_USE_CURRENCY_UNSUPPORTED:
        'O rateio conjunto de créditos de compras e ativos está disponível somente em reais. Revise a moeda dos livros e das fontes; valores em moedas diferentes não podem ser somados diretamente.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_OWNER_BASIS_DRIFT:
        'O custo ou o cronograma do ativo diverge do histórico que sustenta o crédito. Revise o ativo e seus lançamentos antes de recalcular a apuração.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_OWNER_ALREADY_CONSUMED:
        'O custo de origem já foi depreciado ou apropriado. Revise os períodos e os lançamentos afetados pelo fluxo de correção antes de aprovar.',
      FIXED_ASSET_COMMON_USE_CURRENT_ERROR_STATE_CORRECTION_MULTIPERIOD_REQUIRED:
        'Esta correção afeta a suspensão ou a retomada da depreciação e exige revisar todas as competências atingidas. O fluxo atual corrige apenas uma competência e não atende a este caso. Mantenha a apuração em revisão; recalcular sozinho não resolve o bloqueio.',
      FIXED_ASSET_COMMON_USE_NEW_ESTIMATE_REQUIRED:
        'O ajuste cria saldo a depreciar após o fim da vida útil estimada. Revise a vida útil e o valor residual do ativo com a evidência contábil que sustenta essa decisão antes de aprovar.',
      FIXED_ASSET_COMMON_USE_ALLOWANCE_PRESENT:
        'O ativo possui perda de recuperabilidade registrada. A integração dessa perda com o rateio do crédito ainda não está disponível. Mantenha a apuração em revisão.',
      FIXED_ASSET_COMMON_USE_REQUIRES_ONE_POSTED_BOOK:
        'O rateio do crédito exige um único livro de contabilização vinculado ao ativo. Revise os livros do ativo e da entidade.',
      FIXED_ASSET_COMMON_USE_POSTED_BOOK_SCOPE_MISMATCH:
        'O livro de contabilização do ativo não corresponde ao livro da origem ou da apuração. Revise esses vínculos antes de aprovar.',
      TAX_ASSESSMENT_FIXED_ASSET_DISPOSAL_CREDIT_REVERSAL_REQUIRED:
        'A baixa do imobilizado afeta créditos já registrados ou ainda diferidos. A apuração permanece bloqueada até a reversão fiscal vinculada à baixa; essa recuperação ainda não está disponível neste fluxo. Preserve os vínculos do bem e da compra para a revisão contábil.',
      TAX_ASSESSMENT_FIXED_ASSET_CREDIT_TIMING_REQUIRED:
        'Há crédito de imobilizado sem a decisão fiscal ou o vínculo de origem necessário. Revise o bem e a conta a pagar de origem antes de gerar a apuração. Não é seguro tratar o valor integral como crédito de compra comum.',
      TAX_ASSESSMENT_FIXED_ASSET_CREDIT_REVENUE_ALLOCATION_UNSUPPORTED:
        'A apropriação deste crédito de imobilizado ainda não contempla a destinação às receitas informada. Mantenha a classificação real e revise o tratamento fiscal; não há liberação automática desse crédito.',
      TAX_ASSESSMENT_NOT_FOUND:
        'A apuração tributária não foi encontrada. Atualize os dados e tente novamente.',
      TAX_ASSESSMENT_REVISION_NOT_FOUND:
        'A revisão da apuração tributária não foi encontrada. Atualize os dados e tente novamente.',
      TAX_ASSESSMENT_REVISION_NOT_DRAFT:
        'A revisão já saiu do rascunho e não pode ser aprovada novamente. Atualize os dados e use a revisão atual.',
      TAX_ASSESSMENT_SLOT_NOT_FOUND:
        'O catálogo não contém uma conta tributária necessária para esta apuração. Atualize a configuração tributária antes de tentar novamente.',
      TAX_ASSESSMENT_INVALID_AMOUNT:
        'O valor ficou zerado ou negativo após o arredondamento da moeda. Revise o valor informado e tente novamente.',
      TAX_ASSESSMENT_ENTITLEMENT_OVER_ALLOCATED:
        'Outro lançamento consumiu parte do crédito tributário e a alocação excede o saldo atual. Atualize os saldos e aloque somente o valor disponível.',
      TAX_ASSESSMENT_ORIGIN_OVER_CONSUMED:
        'Outro lançamento consumiu parte da origem tributária e o valor solicitado excede o saldo atual. Atualize os saldos e consuma somente o valor disponível.',
      TAX_ASSESSMENT_STATUTORY_BOOK_CURRENCY_INVALID:
        'O livro estatutário brasileiro está com uma moeda funcional incompatível. Configure a moeda exigida pela política contábil antes de apurar.',
      TAX_ENTITLEMENT_NOT_FOUND:
        'O crédito tributário não foi encontrado. Atualize os saldos e tente novamente.',
      TAX_ACCRUAL_ORIGIN_NOT_FOUND:
        'A origem da obrigação tributária não foi encontrada. Atualize os saldos e tente novamente.',
      TAX_ASSESSMENT_TAX_SLOT_MISMATCH: 'A conta selecionada pertence a outra trilha tributária.',
      TAX_ASSESSMENT_TRANSITION_POLICY_UNSUPPORTED:
        'Este cálculo ainda não contempla períodos a partir de janeiro de 2027. Revise a transição para a CBS e eventual PIS/COFINS sobre recebimentos de operações até 2026; essas obrigações não são automaticamente zeradas.',
      TAX_ASSESSMENT_HEAD_OFFICE_NOT_EFFECTIVE:
        'Não há matriz vinculada ao contribuinte no período selecionado. Revise as datas do vínculo ou escolha um período com matriz vigente.',
      TAX_ASSESSMENT_LINKAGE_INVALID:
        'A evidência da apuração pertence a outro contribuinte, revisão, linha ou lançamento.',
      TAX_ASSESSMENT_LINE_AMOUNTS_INVALID:
        'Os valores da linha da apuração não fecham. Recalcule a linha e tente novamente.',
      TAX_ASSESSMENT_MEMBER_LINE_INVALID:
        'A linha da empresa não corresponde ao tributo, vínculo, livro ou valores da apuração.',
      TAX_ASSESSMENT_MEMBER_LINE_DUPLICATE:
        'Esta empresa já possui um resultado para a linha da apuração.',
      TAX_ASSESSMENT_POSTING_INVALID:
        'O lançamento da apuração não corresponde à linha, diário, empresa, livro ou moeda.',
      TAX_ASSESSMENT_SOURCE_CHANGED_RETRY:
        'As fontes da apuração mudaram durante a aprovação. Recalcule e tente novamente.',
      TAX_ASSESSMENT_SOURCE_SYNC_FAILED:
        'Não foi possível sincronizar uma ou mais fontes da apuração. Revise os dados de origem, recalcule a apuração e tente novamente.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_SOURCE_JOURNAL_INVALID:
        'Um crédito de compra de PIS ou COFINS não tem uma provisão válida de contas a pagar para sustentá-lo. A provisão pode não ter sido contabilizada, ter sido estornada ou ter saldo insuficiente. Revise a provisão da conta a pagar de origem, recalcule a apuração e tente aprovar novamente.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_REVENUE_USE_REQUIRED:
        'Um crédito de compra em período com receitas cumulativas e não cumulativas precisa da destinação revisada. Abra a conta a pagar de origem, informe “Destinação do crédito em receitas mistas” e registre a evidência; depois gere a apuração novamente.',
      TAX_ASSESSMENT_POSTING_ACCOUNT_MISSING:
        'Uma conta necessária para contabilizar a apuração não existe ou não aceita lançamentos.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_BALANCE_RECLASSIFICATION_REQUIRED:
        'O mapeamento do crédito de compra mudou desde a provisão. A aprovação não transfere esse saldo automaticamente para a nova conta. Revise o mapeamento e restaure a conta da provisão antes de recalcular e tentar novamente. Uma reclassificação manual, isoladamente, não atualiza o vínculo fiscal.',
      TAX_ASSESSMENT_POSTING_PERIOD_NOT_OPEN:
        'Nenhum período estatutário aberto pode receber o lançamento da apuração.',
      TAX_ASSESSMENT_APPROVAL_BEFORE_PERIOD_START:
        'Este rascunho de período futuro pode ser revisado agora e aprovado a partir do primeiro dia de sua competência fiscal.',
      TAX_ASSESSMENT_ORIGIN_BALANCE_INSUFFICIENT:
        'As origens do passivo apurado não cobrem a compensação ou transferência solicitada.',
      TAX_ASSESSMENT_ORIGIN_PERIOD_MISMATCH:
        'Uma origem do passivo está registrada em mês diferente da apuração. Corrija o período da origem antes de aprovar.',
      TAX_ASSESSMENT_OFFSET_SOURCE_SCOPE_UNSUPPORTED:
        'Esta apuração usa crédito, retenção ou antecipação mantido por uma filial. A aprovação ainda não transfere esse ativo para o livro fiscal da matriz. Mantenha o rascunho para revisão; a transferência contábil desses créditos ainda não está disponível.',
      TAX_ASSESSMENT_AMENDMENT_CLASSIFICATION_REQUIRED:
        'Esta retificação altera o resultado tributário em relação à revisão aprovada. Abra a aprovação, classifique a correção como erro do período corrente ou mudança de estimativa e registre a evidência antes de confirmar.',
      TAX_ASSESSMENT_JOURNAL_NOT_POSTED: 'O lançamento da apuração não foi contabilizado.',
      TAX_ASSESSMENT_REGIME_CHANGE_NOT_RELEASABLE:
        'A mudança de regime não está disponível para uma liberação confirmada de tributos diferidos.',
      TAX_ASSESSMENT_REGIME_CHANGE_POST_2026_RELEASE_UNSUPPORTED:
        'Uma mudança de regime vigente após 2026 não equivale ao recebimento exigido para o saldo residual de PIS/COFINS. A liberação por este evento foi recusada; mantenha o saldo diferido até um fluxo legalmente suportado.',
      TAX_ASSESSMENT_CONFIRMATION_REQUIRED:
        'Uma confirmação humana verificada pelo servidor é obrigatória para esta liberação.',
      TAX_ASSESSMENT_APPROVED_EVIDENCE_IMMUTABLE:
        'A evidência de uma apuração aprovada não pode ser alterada. Crie uma nova revisão.',
      TAX_ASSESSMENT_WARNINGS_REQUIRE_ACKNOWLEDGEMENT:
        'Há um aviso obrigatório da apuração sem confirmação. Revise-o antes de aprovar.',
      TAX_ASSESSMENT_WARNING_ACKNOWLEDGEMENT_INVALID:
        'Este aviso não pode ser confirmado novamente nem alterado depois da confirmação.',
      TAX_ASSESSMENT_BLOCKING_WARNING_PRESENT:
        'Há um bloqueio na apuração que precisa ser resolvido antes da aprovação. Corrija os dados de origem e recalcule.',
      PIS_COFINS_NEGATIVE_MONTH_NO_CARRY:
        'Há base tributária negativa nesta apuração. O valor a recolher do tributo e código de arrecadação afetados foi limitado a zero. Esse resíduo não gera crédito fiscal nem compensação automática.',
      PIS_COFINS_PROVISION_REGIME_MISMATCH:
        'Há provisões calculadas com um regime diferente do histórico fiscal do período. Revise as provisões antes de aprovar.',
      PIS_COFINS_DECLARATION_CORRECTION_REQUIRED:
        'Há receita de uma competência anterior contabilizada neste mês. Revise a retificação das obrigações do mês indicado.',
      DECLARATION_CORRECTION_REQUIRED_OD7:
        'Uma correção de tributo pago exige revisão da declaração do mês original. Revise a recuperação e a obrigação antes de aprovar.',
      PIS_COFINS_PENDING_CASH_RELEASE:
        'Há uma liberação do regime de caixa pendente no mês original. Reabra o mês quando necessário e execute a correção novamente.',
      PIS_COFINS_PENDING_PROVISION:
        'Há uma provisão de PIS ou COFINS pendente. Corrija a configuração ou reabra o mês indicado e execute a correção novamente.',
      PIS_COFINS_GROSS_PENDING_PROVISION:
        'Uma provisão dos tributos sobre a receita continua pendente. Reabra o mês indicado quando necessário e execute a correção novamente.',
      PIS_COFINS_GROSS_PENDING_CASH_RELEASE:
        'Uma liberação do regime de caixa continua pendente no mês original. Reabra esse mês e execute a correção novamente.',
      PIS_COFINS_LEGAL_MONTH_UNRESOLVED:
        'A competência legal de uma receita não pôde ser confirmada pela origem. Revise o documento e a competência antes de aprovar.',
      PIS_COFINS_REVENUE_CLASS_MISSING:
        'Há receita sem Classe de Receita. Classifique a conta e recalcule a apuração.',
      PIS_COFINS_CASH_REVERSAL_WITHOUT_RECEIPT_REVERSAL:
        'Há um cancelamento no regime de caixa sem estorno do recebimento correspondente. Revise o recebimento antes de aprovar.',
      PIS_COFINS_MIXED_REGIME_REVENUE_SPLIT_REQUIRED:
        'O mês não cumulativo contém receita que permanece cumulativa. Separe os valores e os códigos de arrecadação antes de aprovar.',
      PIS_COFINS_CASH_BASIS_REVENUE_OUTSIDE_AR:
        'Há receita do regime de caixa fora de contas a receber. Confirme a data do recebimento antes de aprovar.',
      TAX_ENTITLEMENT_RELEASE_INVALID:
        'A liberação do crédito tributário não corresponde a uma alocação em aberto.',
      TAX_ENTITLEMENT_TRANSITION_INVALID:
        'A transição do crédito tem titular, tributo, moeda ou tipo de destino incompatível.',
      TAX_ACCRUAL_ORIGIN_HAS_ACTIVE_CONSUMPTIONS:
        'A origem tributária possui consumos ativos e não pode ser estornada.',
      TAX_ENTITLEMENT_AUTOMATIC_ALLOCATION_FORBIDDEN:
        'Este saldo segregado, legado ou de tributo pago não pode ser alocado automaticamente.',
      TAX_ENTITLEMENT_ALLOCATION_REVISION_NOT_DRAFT:
        'Créditos tributários só podem ser alocados a uma revisão de apuração em rascunho.',
      TAX_ENTITLEMENT_TAX_DATE_AFTER_PERIOD:
        'A data do crédito tributário é posterior ao período da apuração.',
      TAX_ENTITLEMENT_WITHHOLDING_OUTSIDE_PERIOD:
        'A retenção de cliente só pode compensar o mês da apuração em que foi retida. Selecione a retenção do próprio mês e tente novamente.',
      TAX_ENTITLEMENT_ALLOCATION_REASON_INVALID:
        'Correção e estorno restauram a disponibilidade por um movimento de liberação.',
      TAX_ACCRUAL_ORIGIN_SOURCE_LINE_OVER_ATTRIBUTED:
        'As origens tributárias ativas excedem o crédito da linha do lançamento de origem.',
      TAX_ENTITLEMENT_SOURCE_CONFLICT:
        'A origem do crédito tributário já foi registrada com dados diferentes. Revise a origem antes de tentar novamente.',
      TAX_ASSESSMENT_ALLOCATION_TOTAL_MISMATCH:
        'As alocações do rascunho não correspondem aos créditos, retenções e antecipações das linhas. Reaplique as compensações antes de aprovar.',
      TAX_ASSESSMENT_ENTITLEMENT_CHAIN_CAP_EXCEEDED:
        'O rascunho usaria mais do que o valor atual de um crédito tributário revisado. Recalcule as compensações de todos os períodos afetados.',
      TAX_ASSESSMENT_PRORATION_INVALID:
        'As evidências do rateio por receita estão incompletas ou inválidas. Revise os totais de receita.',
      TAX_ASSESSMENT_LEGACY_TRANSITION_DISABLED:
        'A transição dos créditos legados de 2026 está desabilitada até que um contador registre o artigo de controle dos créditos remanescentes.',
      TAX_ASSESSMENT_PAID_GUIDE_EVIDENCE_MISSING:
        'O valor do tributo não possui evidência durável de guia paga e não pode se tornar um tributo pago a recuperar.',
      TAX_ASSESSMENT_PERIOD_MUST_BE_CALENDAR_MONTH:
        'Selecione o primeiro e o último dia do mesmo mês-calendário e sincronize novamente.',
      TAX_ASSESSMENT_PIS_COFINS_CREDIT_USE_AFTER_EXTINCTION:
        'Créditos de compras de PIS e COFINS não podem ser usados após 31 de dezembro de 2026. Revise a transição do saldo remanescente para créditos legados.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_REGIME_FORBIDDEN:
        'Créditos de compras não podem compensar um período cumulativo de PIS e COFINS. Remova o crédito de compra e recalcule o rascunho.',
      TAX_ASSESSMENT_DECEMBER_APPROVAL_REQUIRED:
        'Aprove a apuração de dezembro de 2026, sem rascunho aberto, antes de transicionar os créditos.',
      TAX_ASSESSMENT_WITHHOLDING_NOT_FULLY_APPLIED:
        'Segregue a retenção somente depois de compensar totalmente o mesmo mês e tributo.',
    },
    assessmentWarnings: {
      PURCHASE_CREDIT_SOURCE_FACT_CHANGED:
        'Um fato de crédito de compra mudou. Revise e confirme a correção antes de aprovar.',
      CUSTOMER_WITHHOLDING_SOURCE_FACT_CHANGED:
        'Um fato de retenção de cliente mudou. Revise e confirme a correção antes de aprovar.',
      TAX_ENTITLEMENT_MOVED_OUT_EXCEEDS_CORRECTED_AMOUNT:
        'O valor já segregado ou convertido em crédito legado supera o valor corrigido da origem. Corrija o saldo segregado ou a recuperar antes de usá-lo.',
    },
    fiscalHistoryErrors: {
      TAXPAYER_FISCAL_HISTORY_MISSING:
        'Não há histórico fiscal válido para o período informado. Cadastre a vigência antes de continuar.',
      TAXPAYER_FISCAL_HISTORY_OVERLAP:
        'Há mais de uma vigência fiscal para o período informado. Corrija a sobreposição antes de continuar.',
      TAXPAYER_FISCAL_HISTORY_DATE_INVALID:
        'A data final da vigência fiscal precisa ser igual ou posterior à data inicial.',
      TAXPAYER_FISCAL_HISTORY_APPROVED_PERIOD_LOCKED:
        'Uma apuração aprovada usa essa vigência fiscal. Reabra ou substitua a apuração antes de alterá-la.',
      TAXPAYER_FISCAL_HISTORY_REGIME_MISMATCH:
        'Matriz e filiais precisam usar o regime fiscal do contribuinte durante toda a vigência informada.',
      TAXPAYER_FISCAL_HISTORY_TARGET_CONFLICT:
        'Escolha entre editar a vigência fiscal ou iniciar a sucessora. As duas ações não podem ser feitas juntas.',
      TAXPAYER_FISCAL_MEMBERSHIP_CHANGED_RETRY:
        'A composição de matriz e filiais mudou durante o salvamento. Atualize os dados e tente novamente.',
      TAXPAYER_FISCAL_PERIOD_GAP:
        'Há um dia sem vigência fiscal no período informado. Complete o histórico antes de continuar.',
      TAXPAYER_FISCAL_PERIOD_OVERLAP:
        'Há um dia coberto por mais de uma vigência fiscal no período informado. Corrija a sobreposição antes de continuar.',
      TAXPAYER_FISCAL_PERIOD_PROFILE_CHANGE:
        'O período informado atravessa uma divisão de vigência fiscal. Divida o período nessa data antes de continuar.',
      TAXPAYER_FISCAL_REGIME_CHANGE_EXCEPTION_REQUIRED:
        'Uma mudança de regime fora de 1º de janeiro exige motivo legal fechado e evidência.',
      TAXPAYER_REGIME_CHANGE_EVENT_CONSUMED:
        'O evento dessa mudança de regime já foi usado por uma liberação fiscal e não pode ser substituído.',
      TAXPAYER_FISCAL_EVIDENCE_REQUIRED:
        'Informe o código e a descrição da evidência fiscal antes de continuar.',
      TAXPAYER_YEARLY_ELECTION_LOCKED:
        'O método de apropriação de créditos de receitas mistas fica fixo durante todo o ano-calendário.',
      TAXPAYER_YEARLY_ELECTION_MISSING:
        'Defina o método anual de apropriação de créditos de receitas mistas antes de continuar.',
      TAXPAYER_YEARLY_ELECTION_YEAR_INVALID: 'O ano-calendário precisa estar entre 2000 e 2100.',
      TAXPAYER_NOT_FOUND:
        'Não encontramos o contribuinte federal. Atualize a página e tente novamente.',
    },
    noPermission: 'Você não tem permissão para alterar estas configurações.',
    noReadPermission:
      'Você não tem permissão para ver os tributos sobre a receita (PIS/COFINS). Peça acesso de leitura às configurações.',
    panel: {
      title: 'Tributos sobre a receita (PIS/COFINS)',
      subtitle: 'PIS e COFINS provisionados junto com cada receita, sem esperar a guia.',
      retry: 'Tentar de novo',
      saveFirst: 'Salve a empresa para configurar os tributos sobre a receita (PIS/COFINS).',
      notApplicableFallback: 'Esta empresa não está sujeita a PIS/COFINS sobre a receita.',
      progress: '{{done}} de {{total}} passos concluídos',
      refreshing: 'Conferindo o plano de contas para o mês escolhido...',
      saveToUpdate: 'Salve para atualizar esta seção',
    },
    step: {
      doneLabel: 'Concluído',
      pendingLabel: 'Pendente',
      lockedLabel: 'Bloqueado',
    },
    checklist: {
      title: 'O que falta',
      refusedTitle: 'Não foi possível iniciar. Ainda falta:',
    },
    warnings: {
      title: 'Confirme antes de salvar',
      description:
        'Revise os avisos abaixo. As configurações de tributos sobre a receita só serão salvas depois que você confirmar.',
      confirm: 'Confirmar e salvar',
      cancel: 'Voltar',
      openStatutoryTreatment: 'Abrir "Tratamento de guias de impostos"',
    },
    months: {
      '01': 'Janeiro',
      '02': 'Fevereiro',
      '03': 'Março',
      '04': 'Abril',
      '05': 'Maio',
      '06': 'Junho',
      '07': 'Julho',
      '08': 'Agosto',
      '09': 'Setembro',
      '10': 'Outubro',
      '11': 'Novembro',
      '12': 'Dezembro',
    },
    classSelect: {
      placeholder: 'Selecione a classe — define PIS/COFINS junto com o regime da entidade',
    },
    coa: {
      label: 'Classe de receita',
      help: 'Define como PIS e COFINS incidem sobre esta conta. Pode ficar em branco enquanto a empresa não usa os tributos sobre a receita.',
      requiredHint:
        'Esta empresa já usa os tributos sobre a receita (desde {{month}}). Escolha a classe desta conta para criá-la.',
      requiredUpFront:
        'Esta empresa já usa os tributos sobre a receita. Escolha a classe desta conta para criá-la.',
      requiredPostableTitle: 'Escolha a classe de receita',
      requiredPostableDescription:
        'A conta {{account}} vai passar a aceitar lançamentos e a empresa já usa os tributos sobre a receita (desde {{month}}). Escolha a classe para continuar.',
      requiredPostableDescriptionNoMonth:
        'A conta {{account}} vai passar a aceitar lançamentos e a empresa já usa os tributos sobre a receita. Escolha a classe para continuar.',
      requiredPostableConfirm: 'Tornar lançável',
      requiredPostableCancel: 'Cancelar',
    },
    classHelp: {
      OPERATING: 'Vendas e serviços do dia a dia. Paga PIS e COFINS pelo regime da empresa.',
      OPERATING_CUMULATIVE:
        'Software próprio de empresa de TI, serviços de educação, saúde e as demais receitas do art. 10 da Lei 10.833/2003: ficam nas alíquotas do regime cumulativo (0,65 % + 3 %), mesmo em empresa não cumulativa. Revenda e software importado não entram.',
      FINANCIAL:
        'Juros e rendimentos de aplicação, e variação de saldo em moeda estrangeira. A alíquota segue o regime da empresa; no regime cumulativo fica fora da base, salvo se for a atividade principal.',
      FINANCIAL_ZERO_RATED:
        'Variação cambial de exportação ou de dívidas (empréstimos, financiamentos) e hedge: alíquota zero. Variação de saldo em moeda estrangeira ou de aplicação é Receita financeira.',
      EQUITY_INTEREST:
        'Juros sobre capital próprio recebidos não usam a alíquota reduzida das receitas financeiras (Decreto 8.426/2015, art. 1º, § 2º).',
      EXPORT:
        'Isenta de PIS e COFINS só quando as duas condições valem:\n1. o cliente está no exterior;\n2. o pagamento traz moeda estrangeira para o país (IN RFB 2.121/2022, art. 20, II).',
      NOT_TAXED:
        'Fora da base: reversão de provisão, recuperação de crédito baixado como perda, resultado de equivalência patrimonial, dividendos e venda de ativo não circulante. Reembolso de despesa cobrado do cliente é receita operacional.',
    },
    classRules: 'Nesta empresa: PIS {{pis}} · COFINS {{cofins}}',
    outcome: {
      TAXED: 'Tributada',
      ZERO_RATED: 'Alíquota zero',
      EXEMPT: 'Isenta',
      OUTSIDE_BASE: 'Fora da base',
    },
    dueBasis: {
      ACCRUAL: 'Competência',
      CASH: 'Caixa',
    },
    classes: {
      title: 'Classe de receitas',
      // The label tooltip says all of it; the step body says the short form (`pendingBody`), the
      // empty select too (`classSelect.placeholder`). No line under the step title (owner, 2026-10-04).
      tooltip:
        'É usada quando uma receita é lançada nesta conta: a classe, junto com o regime tributário da entidade, define se PIS e COFINS incidem e a que alíquota. A classe pertence à conta do plano de contas, por isso é a mesma para todas as entidades que usam este plano. O regime de cada entidade dá o resultado.',
      summaryDone: '{{count}} de {{total}} contas com classe',
      summaryPending: '{{count}} contas sem classe',
      pendingBody:
        'Cada conta de receita precisa de uma classe: é ela que define se PIS e COFINS incidem. A classificação é feita no plano de contas.',
      allClassified: 'Todas as contas de receita têm classe.',
      openInChart: 'Classificar no plano de contas',
      confirmAll: 'Confirmar as {{count}}',
      confirmAllHelp:
        '{{count}} contas têm sugestão de alta confiança (o nome e o grupo da conta concordam).',
      explicitTitle: 'Revise uma a uma',
      explicitHelp: 'A sugestão reduz o imposto ou veio só do nome da conta.',
      confirmExplicit: 'Confirmar estas {{count}}',
      confirmExplicitTitle: 'Confirmar a classe de {{count}} contas',
      confirmExplicitDescription:
        'Cada conta abaixo recebe a classe que aparece ao lado. Confira a lista antes de salvar: algumas classes reduzem o imposto.',
      confirmExplicitAction: 'Salvar as {{count}}',
      // One sentence per class: what the class does to PIS and COFINS. No rate here: the rates of
      // the entity come from its own rules and are appended to the line.
      effect: {
        OPERATING: 'Receita operacional paga PIS e COFINS pelo regime da empresa.',
        OPERATING_CUMULATIVE:
          'Fica nas alíquotas do regime cumulativo, mesmo em empresa não cumulativa.',
        FINANCIAL:
          'Receita financeira segue a alíquota do regime da empresa e, no cumulativo, fica fora da base.',
        FINANCIAL_ZERO_RATED:
          'Variação cambial de exportação, de dívidas e hedge tem alíquota zero.',
        EQUITY_INTEREST:
          'Juros sobre capital próprio não usam a alíquota reduzida das receitas financeiras.',
        EXPORT: 'Exportação é isenta de PIS e COFINS: confira se houve ingresso de divisas.',
        NOT_TAXED: 'Fica fora da base de PIS e COFINS.',
      },
      suggestedTag: 'sugerida',
      reviewTitle: 'Revisar classes de receita',
      reviewHint:
        'Mudar a classe de uma conta vale para as próximas receitas. As provisões já lançadas não mudam.',
      summaryOtherVersions: 'Faltam classes em outros planos de contas',
      confirmRow: 'Confirmar',
      saveRow: 'Salvar',
      noAccounts: 'Esta empresa ainda não tem contas de receita.',
      rowClassLabel: 'Classe de receita da conta {{code}} {{name}}',
      columns: {
        code: 'Código',
        name: 'Conta',
        class: 'Classe de receita',
        action: 'Ação',
      },
      result: {
        updated_one: '{{count}} conta atualizada.',
        updated_other: '{{count}} contas atualizadas.',
        skipped: '{{count}} aguardam a sua confirmação.',
        otherVersion: 'Plano {{label}}: {{count}} contas atualizadas.',
      },
      notPropagated: {
        title: 'Contas de outros planos de contas que precisam de classe',
        line: 'A conta {{code}} tem outro nome neste plano ({{targetName}}; no plano atual: {{sourceName}}). Confirme a classe desta conta.',
      },
      otherVersions: {
        heading: 'Plano {{version}}',
        missingLine: 'A conta {{code}} {{name}} ainda não tem classe.',
        more_one: 'Mais {{count}} conta sem classe neste plano.',
        more_other: 'Mais {{count}} contas sem classe neste plano.',
        publishedNextStep:
          'Este plano de contas já foi publicado e a classe não pode ser alterada por esta tela. Publique uma nova versão do plano de contas com a classe, ou peça ajuda ao suporte.',
        pickMonthLine:
          'Este plano de contas já foi publicado. Escolha início a partir de {{month}}.',
        pickMonth: 'Usar {{month}}',
        // The entity tab keeps one line per other chart version (decision I54); the selects are in
        // the classes dialog of the chart page.
        versionName: '{{name}}, versão {{number}}',
        summaryLine_one: '{{version}} ({{status}}): {{count}} conta sem classe',
        summaryLine_other: '{{version}} ({{status}}): {{count}} contas sem classe',
        statusLabels: {
          DRAFT: 'rascunho',
          PUBLISHED: 'publicada',
          SCHEDULED: 'agendada',
          DEPRECATED: 'arquivada',
        },
      },
    },
    classesDialog: {
      title: 'Classes de receita',
      scope:
        'Classe por conta nesta versão do plano. Vale para todas as entidades que usam esta versão: {{names}}.',
      scopeOtherVersion:
        'Classe por conta nesta versão do plano, que não é o plano ativo de {{names}}.',
      versionClean: 'Todas as contas de receita desta versão do plano já têm classe.',
      versionNotInUse: 'Esta versão do plano não faz parte do plano de contas desta entidade.',
      saveFailed: 'Não foi possível salvar as classes.',
      retry: 'Tentar novamente',
      close: 'Fechar',
    },
    accounts: {
      title: 'Contas de PIS e COFINS',
      // Read-only status: the accounts are created and mapped in one place, the account mapping
      // page (owner, 2026-10-03).
      intro:
        'As contas de dedução e de imposto a recolher que recebem o PIS e a COFINS. Elas são criadas e mapeadas no mapeamento de contas.',
      summaryDone: '{{count}} contas prontas',
      summaryPending: 'As contas de PIS e COFINS ainda não estão prontas',
      openMapping: 'Abrir mapeamento de contas',
      notMapped: 'não mapeada',
      listLabel: 'Contas de PIS e COFINS',
      noChartReason: 'A empresa não tem plano de contas ativo.',
      missingLine_one: 'Falta {{count}} conta. Crie ou mapeie no mapeamento de contas.',
      missingLine_other: 'Faltam {{count}} contas. Crie ou mapeie no mapeamento de contas.',
    },
    start: {
      title: 'Ativar a partir de',
      monthLabel: 'Mês de início',
      yearLabel: 'Ano de início',
      yearColumn: 'Ano',
      monthHelp: 'Padrão: o mês atual. Pode escolher um mês anterior.',
      backfillNote:
        'Os meses de {{month}} até hoje ainda não têm provisão. Depois de iniciar, lance as provisões desses meses.',
      dueBasisLabel: 'Reconhecimento das receitas',
      accrualHelp: 'O imposto nasce no mês da receita.',
      cashCondition: 'O imposto nasce quando o cliente paga.',
      cashUnavailable: 'Disponível só no Lucro Presumido.',
      cashCheckbox: 'A empresa também usa o regime de caixa para IRPJ/CSLL',
      cashNeedsConfirmation: 'Confirme que a empresa usa o regime de caixa também para IRPJ/CSLL.',
      notReadyReason: 'Conclua os passos acima para ativar.',
      lockedStep: 'Conclua o passo {{step}} para ativar.',
      lockedSteps: 'Conclua os passos 1 e 2 para ativar.',
      missingPrefix: 'Falta: {{parts}}',
      missingPart: {
        classes_one: 'classificar {{count}} conta',
        classes_other: 'classificar {{count}} contas',
        accounts: 'criar/mapear as contas de PIS e COFINS',
        other: 'resolver os avisos acima',
      },
      save: 'Ativar provisão',
    },
    started: {
      summary: 'Ativo desde {{month}} · PIS/COFINS {{regime}} · receita reconhecida por {{basis}}',
      ruleLine: '{{class}}: PIS {{pis}} · COFINS {{cofins}}',
      regime: {
        CUMULATIVE: 'cumulativo',
        NON_CUMULATIVE: 'não cumulativo',
      },
      basis: {
        ACCRUAL: 'competência',
        CASH: 'caixa',
      },
      lockedPosted:
        'Já há provisões lançadas: o regime de reconhecimento das receitas fica travado e o mês de início não pode ser adiado.',
      lockedNotYet:
        'Depois que houver provisões lançadas, o regime de reconhecimento das receitas fica travado e o mês de início não pode ser adiado.',
      pendingProvisions_one: '{{count}} provisão ainda não foi lançada',
      pendingProvisions_other: '{{count}} provisões ainda não foram lançadas',
    },
    backfill: {
      counter_one: '{{count}} lançamento aguarda provisão ou estorno de provisão',
      counter_other: '{{count}} lançamentos aguardam provisão ou estorno de provisão',
      releasesCounter_one: '{{count}} liberação aguarda lançamento',
      releasesCounter_other: '{{count}} liberações aguardam lançamento',
      afterFixing: 'Depois de corrigir, clique no botão abaixo.',
      more: 'e mais {{count}}',
      openPeriods: 'Abrir períodos contábeis',
      dialog: {
        description: 'Confira o que será lançado. Nada é lançado antes de você confirmar.',
        run: 'Lançar',
        close: 'Fechar',
        cancel: 'Cancelar',
        previewing: 'Conferindo o que será lançado...',
        keepOpen:
          'Mantenha esta janela aberta até o fim. Se for interrompida, clique de novo em "Lançar provisões pendentes": nada é lançado duas vezes.',
        progressStart: 'Lançando…',
        progress_one: 'Lançando… {{count}} lançamento lido',
        progress_other: 'Lançando… {{count}} lançamentos lidos',
        progressOfTotal_one: 'Lançando… {{scanned}} de {{count}} lançamento',
        progressOfTotal_other: 'Lançando… {{scanned}} de {{count}} lançamentos',
        consentRequired: 'Marque "Entendi" em Guias já aprovadas para lançar.',
        cashRecovery_one:
          'Esta confirmação recupera {{count}} fonte de receita com recebimento conciliado.',
        cashRecovery_other:
          'Esta confirmação recupera {{count}} fontes de receita com recebimento conciliado.',
        cashRecoveryScopeAr: 'Escopo deste lote: faturas a receber.',
        cashRecoveryScopeContract: 'Escopo deste lote: receitas de contratos.',
        cashRecoveryRemaining:
          'Ainda restam {{count}} fontes. Ao concluir este lote, confira e confirme o próximo.',
        cashRecoveryBlocked_one:
          '1 fonte deste lote precisa de correção no recebimento, no período ou na origem. Corrija o item indicado e confira novamente.',
        cashRecoveryBlocked_other:
          '{{count}} fontes deste lote precisam de correção no recebimento, no período ou na origem. Corrija os itens indicados e confira novamente.',
      },
      posted: {
        preview_one: 'Vai provisionar {{count}} lançamento de receita',
        preview_other: 'Vai provisionar {{count}} lançamentos de receita',
        previewFirst_one:
          'Nos primeiros {{scanned}} lançamentos: vai provisionar {{count}} lançamento de receita',
        previewFirst_other:
          'Nos primeiros {{scanned}} lançamentos: vai provisionar {{count}} lançamentos de receita',
        previewNone: 'Nenhuma provisão nova para lançar agora.',
        previewNoneFirst:
          'Nenhuma provisão nos primeiros {{scanned}} lançamentos; a execução continua pelos demais.',
        done: 'Provisões lançadas, por mês',
      },
      mirrors: {
        preview_one: 'Vai estornar {{count}} valor de provisão (um por imposto e conta)',
        preview_other: 'Vai estornar {{count}} valores de provisão (um por imposto e conta)',
        explain:
          'Receita estornada depois do mês da provisão: o imposto provisionado é estornado no mês do estorno, pelo mesmo valor.',
        done: 'Estornos de provisão lançados, por mês',
      },
      releases: {
        preview_one: 'Vai liberar {{count}} valor de imposto no recebimento',
        preview_other: 'Vai liberar {{count}} valores de imposto no recebimento',
        previewReversalsOnly_one: 'Vai estornar {{count}} liberação de imposto',
        previewReversalsOnly_other: 'Vai estornar {{count}} liberações de imposto',
        withReversals_one: '{{title}} e {{count}} estorno de liberação',
        withReversals_other: '{{title}} e {{count}} estornos de liberação',
        explain:
          'Quando o cliente paga, o imposto sai de "a diferir" e vira imposto a recolher no mês do recebimento.',
        done: 'Liberações lançadas, por mês',
        none: 'Nenhuma liberação para lançar agora.',
        pendingTitle: 'Liberações que ficam pendentes',
      },
      blocked: {
        title: 'Meses que ficam sem provisão',
        journals_one: '{{count}} lançamento de receita fica sem provisão',
        journals_other: '{{count}} lançamentos de receita ficam sem provisão',
      },
      owed: {
        title: 'Valores em período fechado: reabra o período para lançar',
      },
      pending: {
        title: 'Provisões que continuam pendentes',
        fallback: 'Falta configuração para lançar estas provisões.',
        journals_one: '{{count}} lançamento',
        journals_other: '{{count}} lançamentos',
        accounts: 'Contas: {{accounts}}',
      },
      skipped: {
        title: 'Sem nova provisão',
        alreadyProvisioned_one: '{{count}} lançamento já está provisionado',
        alreadyProvisioned_other: '{{count}} lançamentos já estão provisionados',
        nettedSameMonth_one: '{{count}} lançamento foi estornado no mesmo mês e resulta em zero',
        nettedSameMonth_other:
          '{{count}} lançamentos foram estornados no mesmo mês e resultam em zero',
        noRevenueBase_one: '{{count}} lançamento não tem base de receita para PIS/COFINS',
        noRevenueBase_other: '{{count}} lançamentos não têm base de receita para PIS/COFINS',
        looksPreTaxed_one:
          '{{count}} lançamento de receita já tem PIS/COFINS no próprio lançamento e não recebe provisão automática',
        looksPreTaxed_other:
          '{{count}} lançamentos de receita já têm PIS/COFINS no próprio lançamento e não recebem provisão automática',
        startMonthChanged_one:
          '{{count}} lançamento ficou para a próxima execução porque o mês de início mudou',
        startMonthChanged_other:
          '{{count}} lançamentos ficaram para a próxima execução porque o mês de início mudou',
        cancelledBeforeStartMonth_one:
          '{{count}} provisão pendente de mês antes do início será cancelada',
        cancelledBeforeStartMonth_other:
          '{{count}} provisões pendentes de meses antes do início serão canceladas',
        cancelledBeforeStartMonthDone_one:
          '{{count}} provisão pendente de mês antes do início foi cancelada',
        cancelledBeforeStartMonthDone_other:
          '{{count}} provisões pendentes de meses antes do início foram canceladas',
        replacedByRepost_one:
          '{{count}} provisão pendente substituída por um novo lançamento será encerrada',
        replacedByRepost_other:
          '{{count}} provisões pendentes substituídas por um novo lançamento serão encerradas',
        replacedByRepostDone_one:
          '{{count}} provisão pendente substituída por um novo lançamento foi encerrada',
        replacedByRepostDone_other:
          '{{count}} provisões pendentes substituídas por um novo lançamento foram encerradas',
      },
      failed: {
        title: 'Não foi possível lançar',
      },
      guides: {
        title: 'Guias já aprovadas',
        consent: 'Entendi o que acontece com cada guia acima.',
        blockedPrefix: '{{month}} fica sem provisão de PIS e COFINS: ',
        number: 'nº {{number}}',
        openGuide: 'abrir a guia',
        unmatched: 'saldo sem origem {{amount}}',
      },
      hasMore: {
        note: 'Esta prévia mostra só os primeiros {{scanned}} lançamentos.',
        continues: 'A execução continua até o fim.',
        continuesWithTotal_one: 'A execução continua até o fim (cerca de {{count}} lançamento).',
        continuesWithTotal_other: 'A execução continua até o fim (cerca de {{count}} lançamentos).',
      },
      setup: {
        title: 'Falta configurar antes de lançar',
        notStarted: 'Defina o mês de início da provisão de PIS/COFINS antes de lançar.',
        unavailable: 'Não foi possível montar a prévia agora. Tente de novo.',
      },
      done: {
        provisions_one: '{{count}} lançamento de receita provisionado',
        provisions_other: '{{count}} lançamentos de receita provisionados',
        reversals_one: '{{count}} valor de estorno lançado',
        reversals_other: '{{count}} valores de estorno lançados',
        cancelled_one: '{{count}} provisão pendente cancelada',
        cancelled_other: '{{count}} provisões pendentes canceladas',
        closed_one: '{{count}} provisão pendente encerrada',
        closed_other: '{{count}} provisões pendentes encerradas',
        releases_one: '{{count}} liberação lançada',
        releases_other: '{{count}} liberações lançadas',
        releaseReversals_one: '{{count}} estorno de liberação lançado',
        releaseReversals_other: '{{count}} estornos de liberação lançados',
        releaseFailedKept: 'As provisões desta execução ficaram lançadas.',
        and: 'e',
        summary: 'Pronto: {{summary}}.',
        nothing: 'Pronto: não havia nada novo para lançar.',
        nothingPosted: 'Nada foi lançado. Veja abaixo o que ficou pendente.',
        stopped: 'Execução interrompida. Até aqui: {{summary}}.',
        stoppedNothing: 'Execução interrompida. Nada foi lançado até aqui.',
        stoppedEarly:
          'O que já foi lançado está salvo e não se repete. Clique de novo em "Lançar provisões pendentes" para continuar.',
      },
      errors: {
        alreadyRunning: 'Outra execução está em andamento. Tente de novo em instantes.',
        guidesNeedAck:
          'Há guias já aprovadas para estes meses. Leia a lista e marque "Entendi" para continuar.',
        invalidCursor:
          'Confira a prévia nova e clique em Lançar para continuar. O que já foi lançado não se repete.',
        invalidCursorRefreshed: 'A prévia foi atualizada. O que já foi lançado não se repete.',
        postedSoFar: 'Já lançado nesta execução: {{summary}}.',
        stalled:
          'A execução parou porque não avançou para os lançamentos seguintes. Clique de novo em "Lançar provisões pendentes".',
        unavailable:
          'A execução não devolveu resultado. Clique de novo em "Lançar provisões pendentes": nada é lançado duas vezes.',
      },
    },
    rules: {
      toggle: 'Ver regras e base legal',
      columns: {
        class: 'Classe de receita',
      },
      source: {
        DEFAULT: 'Padrão legal',
        TENANT_OVERRIDE: 'Regra da organização',
        ENTITY_OVERRIDE: 'Regra da entidade',
      },
      noRule: 'Sem regra na data',
      validFrom: 'Vale desde {{from}}',
      validBetween: 'Vale de {{from}} a {{until}}',
      asOf: 'Regras em vigor em {{date}}.',
      manage: 'Gerenciar regras',
      viewAll: 'Ver todas as regras',
      end: 'Encerrar',
      endTitle: 'Encerrar esta regra?',
      endDescriptionOrganization:
        'Depois da data escolhida, o padrão legal volta a valer para todas as entidades do país.',
      endDescriptionEntity:
        'Depois da data escolhida, a regra da organização ou o padrão legal volta a valer.',
      endConfirm: 'Encerrar regra',
      endCancel: 'Manter',
      endDateLabel: 'A regra vale até',
      endDateHelp: 'Inclusive. Provisões já lançadas não mudam.',
      endDateMaxHelp:
        'Inclusive. Só dá para antecipar o fim: hoje a regra vale até {{until}}. Provisões já lançadas não mudam.',
    },
    override: {
      classLabel: 'Classe de receita',
      taxLabel: 'Tributo',
      outcomeLabel: 'Resultado',
      rateLabel: 'Alíquota (%)',
      rateHelp: 'Digite em percentual, por exemplo 2,00.',
      rateEcho: '= {{percent}}',
      rateMaybe: 'Você quis dizer {{percent}}?',
      reasonLabel: 'Base legal',
      reasonPlaceholder: 'Ex.: Decisão judicial, processo nº ...',
      save: 'Salvar regra',
      cancel: 'Cancelar',
      errors: {
        revenueClass: 'Escolha a classe de receita.',
        validFrom: 'Informe desde quando a regra vale.',
        legalBasis: 'Informe a base legal da regra.',
        rate: {
          EMPTY: 'Informe a alíquota em percentual.',
          INVALID: 'Alíquota inválida. Use um número, por exemplo 2,00.',
          ZERO: 'A alíquota deve ser maior que zero. Para zero, escolha "Alíquota zero".',
          TOO_HIGH: 'Alíquota acima de 25 %. Confira se digitou em percentual.',
        },
      },
    },
    rulesPage: {
      title: 'Tributos sobre receita',
      subtitle:
        'PIS e COFINS sobre a receita, por país: o padrão legal e as regras da organização ou das entidades.',
      filters: {
        country: 'País',
        entity: 'Entidade',
        regime: 'Regime',
        class: 'Classe',
        tax: 'Tributo',
        asOf: 'Válidas em',
        allCountries: 'Todos os países',
        allEntities: 'Todas as entidades',
        allRegimes: 'Todos os regimes',
        allClasses: 'Todas as classes',
        allTaxes: 'Todos os tributos',
        toggle: 'Filtros',
      },
      columns: {
        country: 'País',
        regime: 'Regime',
        class: 'Classe',
        tax: 'Tributo',
        result: 'Resultado',
        validity: 'Vigência',
        source: 'Origem',
        legalBasis: 'Base legal',
        actions: 'Ações',
      },
      scope: {
        ORGANIZATION: 'Organização',
      },
      regime: {
        CUMULATIVE: 'Cumulativo',
        NON_CUMULATIVE: 'Não cumulativo',
      },
      loading: 'Carregando as regras...',
      loadError: 'Não foi possível carregar as regras. {{message}}',
      empty: {
        title: 'Nenhuma regra nesta data',
        description: 'Mude os filtros, a data em "Válidas em" ou crie uma regra.',
      },
      entityNotFound:
        'Não encontramos a entidade do link. Mostrando as regras de todas as entidades.',
      entityNotice: {
        noRegime: 'Esta entidade ainda não tem regime de PIS/COFINS.',
        setRegime: 'Definir em Regime & Alíquotas',
      },
      // The hint shows only when at least one rule was created: "Salvar e criar a de COFINS" can
      // make it two or more, so each hint has a singular and a plural form.
      createdHint: {
        future_one: "Criada, válida a partir de {{date}} — ajuste 'Válidas em' para vê-la.",
        future_other:
          "{{count}} regras criadas, válidas a partir de {{date}} — ajuste 'Válidas em' para vê-las.",
        otherEntities_one: 'Criada para entidades fora do filtro atual.',
        otherEntities_other: '{{count}} regras criadas para entidades fora do filtro atual.',
        filtered_one: 'Criada, mas os filtros atuais não a mostram.',
        filtered_other: '{{count}} regras criadas, mas os filtros atuais não as mostram.',
      },
      // Portuguese counts 0 as singular for plurals; "0 regras" is what a person writes.
      count_zero: '{{count}} regras',
      count_one: '{{count}} regra',
      count_other: '{{count}} regras',
      created_one: '{{count}} regra criada',
      created_other: '{{count}} regras criadas',
      readOnly: 'Somente leitura',
      newRule: 'Nova regra',
      endSummary: '{{class}} · {{tax}} · {{scope}}',
      dialog: {
        title: 'Nova regra',
        help: 'Vale a partir da data escolhida e não muda provisões já lançadas. Informe a base legal.',
        countryLabel: 'País',
        scopeLabel: 'Escopo',
        scopeEntities: 'Entidades',
        countryDisabledReason: 'Sem tributos sobre a receita para este país ainda',
        entitiesLabel: 'Entidades (país: {{country}})',
        selectAll: 'Selecionar todas',
        clear: 'Limpar',
        noEntities: 'Nenhuma entidade com país {{country}}.',
        maxEntities: 'Máx. 100 entidades por regra.',
        organizationHelp:
          'Vale para todas as entidades do país {{country}} nesse regime, exceto as que têm regra própria.',
        regimeLabel: 'Regime de PIS/COFINS',
        regimePlaceholder: 'Escolha o regime',
        validFromLabel: 'Válida de',
        validUntilLabel: 'Válida até',
        validUntilHelp: 'Opcional. Em branco, a regra não tem data final.',
        saving: 'Salvando...',
        saveAndNext: 'Salvar e criar a de {{tax}}',
        regimeMismatch:
          '{{entity}} é {{company}} ({{regime}}); esta regra é do regime {{ruleRegime}} e não vai se aplicar a ela.',
        companyRegime: {
          SIMPLES_NACIONAL: 'Simples Nacional',
          LUCRO_PRESUMIDO: 'Lucro Presumido',
          LUCRO_REAL: 'Lucro Real',
        },
        errors: {
          entities: 'Escolha ao menos uma entidade.',
          regime: 'Escolha o regime.',
          validUntil: 'A data final não pode ser anterior à data inicial.',
        },
      },
    },
  },
};

export const revenueTaxEn: typeof revenueTaxPtBr = {
  revenueTax: {
    assessmentErrors: {
      TAX_ASSESSMENT_PURCHASE_CREDIT_OWNER_BOOK_MISMATCH:
        'The asset, source provision and assessment books do not reconcile. Review these links before approval.',
      TAX_ASSESSMENT_COMMON_USE_DEPRECIATION_CORRECTION_DRIFT:
        'Depreciation history changed after the correction was prepared. Refresh the assessment and review the postings and original period before retrying.',
      TAX_ASSESSMENT_COMMON_USE_ACCOUNTING_TREATMENT_REQUIRED:
        'Prorating the asset credit changes its cost. Select the accounting treatment and record supporting evidence during approval.',
      TAX_ASSESSMENT_COMMON_USE_PRIOR_PERIOD_CORRECTION_UNSUPPORTED:
        'Correcting this allocation requires review of the original period and depreciation history. Preserve the error classification and review the affected periods before approval.',
      TAX_ASSESSMENT_ACCOUNTING_CORRECTION_EVIDENCE_REQUIRED:
        'Enter the reference and description of the evidence supporting the selected accounting treatment.',
      TAX_ASSESSMENT_PRIOR_PERIOD_REOPEN_REQUIRED:
        'To correct this error, reopen the original accounting period and retry assessment approval.',
      TAX_ASSESSMENT_PRIOR_AUTHORIZED_RESTATEMENT_REQUIRED:
        'This error affects already authorized financial statements and requires comparative restatement. That workflow is not available yet; keep the assessment under review.',
      TAX_ASSESSMENT_FIXED_ASSET_COMMON_USE_CURRENCY_UNSUPPORTED:
        'Joint proration of purchase and asset credits is available only in BRL. Review book and source currencies; amounts in different currencies cannot be added directly.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_OWNER_BASIS_DRIFT:
        'The asset cost or schedule differs from the history supporting the credit. Review the asset and its postings before recalculating the assessment.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_OWNER_ALREADY_CONSUMED:
        'The source cost has already been depreciated or recognized. Review the periods and postings affected by the correction workflow before approval.',
      FIXED_ASSET_COMMON_USE_CURRENT_ERROR_STATE_CORRECTION_MULTIPERIOD_REQUIRED:
        'This correction affects depreciation suspension or resumption and requires reviewing every affected period. The current correction flow handles only one period and cannot process this case. Keep the assessment under review; recalculation alone cannot resolve the blocker.',
      FIXED_ASSET_COMMON_USE_NEW_ESTIMATE_REQUIRED:
        'The adjustment creates a depreciable balance after the estimated useful life has ended. Review the asset useful life and residual value with supporting accounting evidence before approval.',
      FIXED_ASSET_COMMON_USE_ALLOWANCE_PRESENT:
        'The asset has a recorded recoverability loss. Integration of that loss with credit proration is not available yet. Keep the assessment under review.',
      FIXED_ASSET_COMMON_USE_REQUIRES_ONE_POSTED_BOOK:
        'Credit proration requires exactly one posting book linked to the asset. Review the asset and entity books.',
      FIXED_ASSET_COMMON_USE_POSTED_BOOK_SCOPE_MISMATCH:
        'The asset posting book does not match the source or assessment book. Review these links before approval.',
      TAX_ASSESSMENT_FIXED_ASSET_DISPOSAL_CREDIT_REVERSAL_REQUIRED:
        'The asset disposal affects recorded or deferred credits. Assessment remains blocked until a tax reversal linked to the disposal is recorded; this recovery is not yet available in this flow. Preserve the asset and purchase links for accounting review.',
      TAX_ASSESSMENT_FIXED_ASSET_CREDIT_TIMING_REQUIRED:
        'A fixed-asset credit is missing its tax-method decision or required source linkage. Review the asset and source bill before generating the assessment. The full amount cannot safely be treated as an ordinary purchase credit.',
      TAX_ASSESSMENT_FIXED_ASSET_CREDIT_REVENUE_ALLOCATION_UNSUPPORTED:
        'This fixed-asset credit method does not yet support the recorded revenue use. Preserve the actual classification and review the tax treatment; this credit cannot be released automatically.',
      TAX_ASSESSMENT_NOT_FOUND: 'The tax assessment was not found. Refresh the data and try again.',
      TAX_ASSESSMENT_REVISION_NOT_FOUND:
        'The tax assessment revision was not found. Refresh the data and try again.',
      TAX_ASSESSMENT_REVISION_NOT_DRAFT:
        'The revision is no longer a draft and cannot be approved again. Refresh the data and use the current revision.',
      TAX_ASSESSMENT_SLOT_NOT_FOUND:
        'The catalog is missing a tax account required by this assessment. Update the tax configuration before trying again.',
      TAX_ASSESSMENT_INVALID_AMOUNT:
        'The amount became zero or negative after currency rounding. Review the amount and try again.',
      TAX_ASSESSMENT_ENTITLEMENT_OVER_ALLOCATED:
        'Another posting consumed part of the tax entitlement, so this allocation exceeds the current balance. Refresh the balances and allocate only the available amount.',
      TAX_ASSESSMENT_ORIGIN_OVER_CONSUMED:
        'Another posting consumed part of the tax origin, so the requested amount exceeds the current balance. Refresh the balances and consume only the available amount.',
      TAX_ASSESSMENT_STATUTORY_BOOK_CURRENCY_INVALID:
        'The Brazilian statutory book has an incompatible functional currency. Configure the currency required by accounting policy before assessing tax.',
      TAX_ENTITLEMENT_NOT_FOUND:
        'The tax entitlement was not found. Refresh balances and try again.',
      TAX_ACCRUAL_ORIGIN_NOT_FOUND:
        'The tax accrual origin was not found. Refresh balances and try again.',
      TAX_ASSESSMENT_TAX_SLOT_MISMATCH: 'The selected account slot belongs to another tax rail.',
      TAX_ASSESSMENT_TRANSITION_POLICY_UNSUPPORTED:
        'This calculator does not yet support periods from January 2027. Review the CBS transition and any PIS/COFINS due on receipts from operations through 2026; these obligations are not automatically zero.',
      TAX_ASSESSMENT_HEAD_OFFICE_NOT_EFFECTIVE:
        'No head office is linked to this taxpayer for the selected period. Review the membership dates or choose a period with an effective head office.',
      TAX_ASSESSMENT_LINKAGE_INVALID:
        'The assessment evidence belongs to another taxpayer, revision, line, or journal.',
      TAX_ASSESSMENT_LINE_AMOUNTS_INVALID:
        'The assessment line amounts do not reconcile. Recalculate the line and try again.',
      TAX_ASSESSMENT_MEMBER_LINE_INVALID:
        'The member assessment line does not match its tax, membership, book, or amounts.',
      TAX_ASSESSMENT_MEMBER_LINE_DUPLICATE:
        'This member already has a result for the assessment line.',
      TAX_ASSESSMENT_POSTING_INVALID:
        'The assessment posting does not match its line, journal, entity, book, or currency.',
      TAX_ASSESSMENT_SOURCE_CHANGED_RETRY:
        'The assessment sources changed during approval. Recalculate and try again.',
      TAX_ASSESSMENT_SOURCE_SYNC_FAILED:
        'One or more assessment sources could not be synchronized. Review the source data, recalculate the assessment, and try again.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_SOURCE_JOURNAL_INVALID:
        'A PIS or COFINS purchase credit has no valid accounts payable provision supporting it. The provision may be unposted, reversed, or have an insufficient balance. Review the originating accounts payable provision, recalculate the assessment, and try approval again.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_REVENUE_USE_REQUIRED:
        'A purchase credit in a period with cumulative and non-cumulative revenue needs reviewed allocation. Open the originating bill, complete “Credit allocation for mixed revenue” with evidence, then generate the assessment again.',
      TAX_ASSESSMENT_POSTING_ACCOUNT_MISSING:
        'A required assessment posting account is missing or not postable.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_BALANCE_RECLASSIFICATION_REQUIRED:
        'The purchase-credit mapping changed after the provision was posted. Approval does not automatically transfer this balance to the new account. Review the mapping and restore the provision account before recalculating and retrying. A manual reclassification alone does not update the fiscal link.',
      TAX_ASSESSMENT_POSTING_PERIOD_NOT_OPEN:
        'No open statutory period can receive the assessment posting.',
      TAX_ASSESSMENT_APPROVAL_BEFORE_PERIOD_START:
        'This future-period draft can be reviewed now and approved from the first day of its legal period.',
      TAX_ASSESSMENT_ORIGIN_BALANCE_INSUFFICIENT:
        'The assessed liability origins do not cover the requested offset or transfer.',
      TAX_ASSESSMENT_ORIGIN_PERIOD_MISMATCH:
        'A liability origin is recorded in a different month from the assessment. Repair the origin period before approval.',
      TAX_ASSESSMENT_OFFSET_SOURCE_SCOPE_UNSUPPORTED:
        'This assessment uses a credit, withholding, or advance held by a branch. Approval cannot transfer that recoverable to the head-office statutory book yet. Keep the draft for review; accounting transfer of these credits is not available yet.',
      TAX_ASSESSMENT_AMENDMENT_CLASSIFICATION_REQUIRED:
        'This amendment changes tax profit or loss from the approved revision. Open approval, classify the correction as a current-period error or estimate change, and record the evidence before confirming.',
      TAX_ASSESSMENT_JOURNAL_NOT_POSTED: 'The assessment journal was not posted.',
      TAX_ASSESSMENT_REGIME_CHANGE_NOT_RELEASABLE:
        'The regime change is not available for a confirmed deferred-tax release.',
      TAX_ASSESSMENT_REGIME_CHANGE_POST_2026_RELEASE_UNSUPPORTED:
        'A regime change effective after 2026 is not the receipt required for residual PIS/COFINS. Release by this event was refused; keep the balance deferred until a legally supported workflow applies.',
      TAX_ASSESSMENT_CONFIRMATION_REQUIRED:
        'A server-verified human confirmation is required for this release.',
      TAX_ASSESSMENT_APPROVED_EVIDENCE_IMMUTABLE:
        'Approved assessment evidence cannot be changed. Create a new revision instead.',
      TAX_ASSESSMENT_WARNINGS_REQUIRE_ACKNOWLEDGEMENT:
        'A required assessment warning has not been acknowledged. Review it before approval.',
      TAX_ASSESSMENT_WARNING_ACKNOWLEDGEMENT_INVALID:
        'This warning cannot be acknowledged again or changed after acknowledgement.',
      TAX_ASSESSMENT_BLOCKING_WARNING_PRESENT:
        'A blocking assessment warning must be resolved before approval. Correct the source facts and recalculate.',
      PIS_COFINS_NEGATIVE_MONTH_NO_CARRY:
        'This assessment contains a negative tax base. Payable for the affected tax and collection code was floored at zero. This residual does not create a tax credit or automatic carry.',
      PIS_COFINS_PROVISION_REGIME_MISMATCH:
        'Some provisions were calculated under a regime different from the period fiscal history. Review them before approval.',
      PIS_COFINS_DECLARATION_CORRECTION_REQUIRED:
        'Revenue from an earlier legal month was posted in this month. Review the required correction for the named month.',
      DECLARATION_CORRECTION_REQUIRED_OD7:
        'A paid-tax correction requires review of the original month declaration. Review the recovery and obligation before approval.',
      PIS_COFINS_PENDING_CASH_RELEASE:
        'A cash-basis release is pending in its original month. Reopen that month when required and run the heal again.',
      PIS_COFINS_PENDING_PROVISION:
        'A PIS or COFINS provision is pending. Correct the setup or reopen the named month and run the heal again.',
      PIS_COFINS_GROSS_PENDING_PROVISION:
        'A Revenue Tax provision is still pending. Reopen the named month when required and run the heal again.',
      PIS_COFINS_GROSS_PENDING_CASH_RELEASE:
        'A cash-basis release is still pending in its original month. Reopen that month and run the heal again.',
      PIS_COFINS_LEGAL_MONTH_UNRESOLVED:
        'The legal month of a revenue source could not be confirmed. Review the source document and legal month before approval.',
      PIS_COFINS_REVENUE_CLASS_MISSING:
        'Revenue has no Revenue Class. Classify the account and recalculate the assessment.',
      PIS_COFINS_CASH_REVERSAL_WITHOUT_RECEIPT_REVERSAL:
        'A cash-basis cancellation has no matching receipt reversal. Review the receipt before approval.',
      PIS_COFINS_MIXED_REGIME_REVENUE_SPLIT_REQUIRED:
        'The non-cumulative month contains revenue that remains cumulative. Split the amounts and collection codes before approval.',
      PIS_COFINS_CASH_BASIS_REVENUE_OUTSIDE_AR:
        'Cash-basis revenue was posted outside accounts receivable. Confirm its receipt date before approval.',
      TAX_ENTITLEMENT_RELEASE_INVALID: 'The entitlement release does not match an open allocation.',
      TAX_ENTITLEMENT_TRANSITION_INVALID:
        'The entitlement transition has an incompatible owner, tax, currency, or destination type.',
      TAX_ACCRUAL_ORIGIN_HAS_ACTIVE_CONSUMPTIONS:
        'The tax origin has active consumptions and cannot be reversed.',
      TAX_ENTITLEMENT_AUTOMATIC_ALLOCATION_FORBIDDEN:
        'This segregated, legacy, or paid-tax balance cannot be allocated automatically.',
      TAX_ENTITLEMENT_ALLOCATION_REVISION_NOT_DRAFT:
        'Entitlements can only be allocated to a draft assessment revision.',
      TAX_ENTITLEMENT_TAX_DATE_AFTER_PERIOD:
        'The entitlement tax date is after the assessment period.',
      TAX_ENTITLEMENT_WITHHOLDING_OUTSIDE_PERIOD:
        'Customer withholding can only offset the month in which it was withheld. Select withholding from that same month and retry.',
      TAX_ENTITLEMENT_ALLOCATION_REASON_INVALID:
        'Correction and reversal restore availability through a release movement.',
      TAX_ACCRUAL_ORIGIN_SOURCE_LINE_OVER_ATTRIBUTED:
        'Active tax origins exceed the source journal line credit.',
      TAX_ENTITLEMENT_SOURCE_CONFLICT:
        'The tax entitlement source was already recorded with different facts. Review the source before retrying.',
      TAX_ASSESSMENT_ALLOCATION_TOTAL_MISMATCH:
        'Draft allocations do not match the line credit, withholding, and advance amounts. Reapply the offsets before approval.',
      TAX_ASSESSMENT_ENTITLEMENT_CHAIN_CAP_EXCEEDED:
        'The draft would use more than the current amount of a revised tax entitlement. Recalculate the offsets across every affected period.',
      TAX_ASSESSMENT_PRORATION_INVALID:
        'The revenue proration evidence is incomplete or invalid. Review the revenue totals.',
      TAX_ASSESSMENT_LEGACY_TRANSITION_DISABLED:
        'The 2026 legacy-credit transition is disabled until an accountant records the controlling article for remaining credits.',
      TAX_ASSESSMENT_PAID_GUIDE_EVIDENCE_MISSING:
        'The tax amount has no durable paid-guide evidence and cannot become a paid-tax recoverable.',
      TAX_ASSESSMENT_PERIOD_MUST_BE_CALENDAR_MONTH:
        'Select the first and last day of the same calendar month and synchronize again.',
      TAX_ASSESSMENT_PIS_COFINS_CREDIT_USE_AFTER_EXTINCTION:
        'PIS and COFINS purchase credits cannot be used after 31 December 2026. Review the legacy-credit transition for the remaining balance.',
      TAX_ASSESSMENT_PURCHASE_CREDIT_REGIME_FORBIDDEN:
        'Purchase credits cannot offset a cumulative PIS and COFINS period. Remove the purchase-credit offset and recalculate the draft.',
      TAX_ASSESSMENT_DECEMBER_APPROVAL_REQUIRED:
        'Approve the December 2026 assessment with no open draft before transitioning credits.',
      TAX_ASSESSMENT_WITHHOLDING_NOT_FULLY_APPLIED:
        'Segregate withholding only after fully offsetting the same month and tax.',
    },
    assessmentWarnings: {
      PURCHASE_CREDIT_SOURCE_FACT_CHANGED:
        'A purchase-credit fact changed. Review and confirm the correction before approval.',
      CUSTOMER_WITHHOLDING_SOURCE_FACT_CHANGED:
        'A customer-withholding fact changed. Review and confirm the correction before approval.',
      TAX_ENTITLEMENT_MOVED_OUT_EXCEEDS_CORRECTED_AMOUNT:
        'The amount already segregated or converted to a legacy credit exceeds the corrected source amount. Correct the segregated or recoverable balance before using it.',
    },
    fiscalHistoryErrors: {
      TAXPAYER_FISCAL_HISTORY_MISSING:
        'No fiscal history covers this assessment date. Add the validity period before continuing.',
      TAXPAYER_FISCAL_HISTORY_OVERLAP:
        'More than one fiscal period covers this assessment date. Correct the overlap before continuing.',
      TAXPAYER_FISCAL_HISTORY_DATE_INVALID:
        'The fiscal validity end date must be on or after its start date.',
      TAXPAYER_FISCAL_HISTORY_APPROVED_PERIOD_LOCKED:
        'An approved assessment uses this fiscal period. Reopen or replace it before changing the period.',
      TAXPAYER_FISCAL_HISTORY_REGIME_MISMATCH:
        'Head office and branches must use the Taxpayer fiscal regime throughout this validity period.',
      TAXPAYER_FISCAL_HISTORY_TARGET_CONFLICT:
        'Choose either to edit the fiscal period or start its successor. These actions cannot be combined.',
      TAXPAYER_FISCAL_MEMBERSHIP_CHANGED_RETRY:
        'The head-office and branch composition changed while saving. Refresh the data and try again.',
      TAXPAYER_FISCAL_PERIOD_GAP:
        'A day in the requested period has no fiscal profile. Complete the history before continuing.',
      TAXPAYER_FISCAL_PERIOD_OVERLAP:
        'A day in the requested period has more than one fiscal profile. Correct the overlap before continuing.',
      TAXPAYER_FISCAL_PERIOD_PROFILE_CHANGE:
        'The requested period crosses a fiscal-history period split. Split the request on that date.',
      TAXPAYER_FISCAL_REGIME_CHANGE_EXCEPTION_REQUIRED:
        'A tax-regime change outside January 1 requires a closed legal reason and evidence.',
      TAXPAYER_REGIME_CHANGE_EVENT_CONSUMED:
        'This regime-change event has already been used by a tax release and cannot be superseded.',
      TAXPAYER_FISCAL_EVIDENCE_REQUIRED:
        'Enter the fiscal evidence code and description before continuing.',
      TAXPAYER_YEARLY_ELECTION_LOCKED:
        'The mixed-revenue credit appropriation method is fixed for the calendar year.',
      TAXPAYER_YEARLY_ELECTION_MISSING:
        'Set the yearly mixed-revenue credit appropriation method before continuing.',
      TAXPAYER_YEARLY_ELECTION_YEAR_INVALID: 'The calendar year must be between 2000 and 2100.',
      TAXPAYER_NOT_FOUND: 'The federal taxpayer was not found. Refresh and try again.',
    },
    noPermission: 'You do not have permission to change these settings.',
    noReadPermission:
      'You do not have permission to view the taxes on revenue (PIS/COFINS). Ask for read access to the settings.',
    panel: {
      title: 'Taxes on revenue (PIS/COFINS)',
      subtitle:
        'PIS and COFINS are provisioned with each revenue, without waiting for the tax slip.',
      retry: 'Try again',
      saveFirst: 'Save the entity to set up taxes on revenue (PIS/COFINS).',
      notApplicableFallback: 'This entity is not subject to PIS/COFINS on revenue.',
      progress: '{{done}} of {{total}} steps done',
      refreshing: 'Checking the chart of accounts for the chosen month...',
      saveToUpdate: 'Save to update this section',
    },
    step: {
      doneLabel: 'Done',
      pendingLabel: 'Pending',
      lockedLabel: 'Locked',
    },
    checklist: {
      title: 'What is missing',
      refusedTitle: 'Could not start. Still missing:',
    },
    warnings: {
      title: 'Confirm before saving',
      description:
        'Review the warnings below. The revenue tax settings are saved only after you confirm.',
      confirm: 'Confirm and save',
      cancel: 'Go back',
      openStatutoryTreatment: 'Open "Tax slip treatment"',
    },
    months: {
      '01': 'January',
      '02': 'February',
      '03': 'March',
      '04': 'April',
      '05': 'May',
      '06': 'June',
      '07': 'July',
      '08': 'August',
      '09': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    },
    classSelect: {
      placeholder: 'Pick the class — sets PIS/COFINS together with the entity regime',
    },
    coa: {
      label: 'Revenue class',
      help: 'Sets how PIS and COFINS apply to this account. It can stay empty while the entity does not use Revenue Tax.',
      requiredHint:
        'This entity already uses Revenue Tax (since {{month}}). Choose the class of this account to create it.',
      requiredUpFront:
        'This entity already uses Revenue Tax. Choose the class of this account to create it.',
      requiredPostableTitle: 'Choose the revenue class',
      requiredPostableDescription:
        'Account {{account}} is about to accept postings and the entity already uses Revenue Tax (since {{month}}). Choose the class to continue.',
      requiredPostableDescriptionNoMonth:
        'Account {{account}} is about to accept postings and the entity already uses Revenue Tax. Choose the class to continue.',
      requiredPostableConfirm: 'Make postable',
      requiredPostableCancel: 'Cancel',
    },
    classHelp: {
      OPERATING: 'Everyday sales and services. Pays PIS and COFINS under the company regime.',
      OPERATING_CUMULATIVE:
        'Own software of an IT company, education and health services and the other revenues of art. 10 of Lei 10.833/2003: stay on the cumulative rates (0.65 % + 3 %), even in a non-cumulative company. Resale and imported software do not qualify.',
      FINANCIAL:
        'Interest and investment yields, and variation of a foreign currency balance. The rate follows the company regime; under the cumulative regime it is outside the base, unless it is the main activity.',
      FINANCIAL_ZERO_RATED:
        'Foreign exchange variation on exports or on debts (loans, financing) and hedge: zero rate. Variation of a foreign currency balance or of an investment is financial revenue.',
      EQUITY_INTEREST:
        'Interest on equity received does not get the reduced rate of financial revenue (Decreto 8.426/2015, art. 1, par. 2).',
      EXPORT:
        'Exempt from PIS and COFINS only when both conditions hold:\n1. the customer is abroad;\n2. the payment brings foreign currency into the country (IN RFB 2.121/2022, art. 20, II).',
      NOT_TAXED:
        'Outside the base: reversal of provisions, recovery of credit written off as a loss, equity-method result, dividends and sale of non-current assets. An expense reimbursement charged to the customer is operating revenue.',
    },
    classRules: 'In this company: PIS {{pis}} · COFINS {{cofins}}',
    outcome: {
      TAXED: 'Taxed',
      ZERO_RATED: 'Zero rate',
      EXEMPT: 'Exempt',
      OUTSIDE_BASE: 'Outside the base',
    },
    dueBasis: {
      ACCRUAL: 'Accrual',
      CASH: 'Cash',
    },
    classes: {
      title: 'Revenue classes',
      tooltip:
        "Used when revenue posts to this account: the class, together with the entity's tax regime, decides whether PIS and COFINS apply and at what rate. The class belongs to the chart account, so it is the same for every entity that shares this chart. Each entity's regime gives the result.",
      summaryDone: '{{count}} of {{total}} accounts have a class',
      summaryPending: '{{count}} accounts without a class',
      pendingBody:
        'Every revenue account needs a class: it decides whether PIS and COFINS apply. Classifying is done in the chart of accounts.',
      allClassified: 'Every revenue account has a class.',
      openInChart: 'Classify in the chart of accounts',
      confirmAll: 'Confirm all {{count}}',
      confirmAllHelp:
        '{{count}} accounts have a high-confidence suggestion (the name and the group of the account agree).',
      explicitTitle: 'Review one by one',
      explicitHelp: 'The suggestion lowers the tax or came only from the name of the account.',
      confirmExplicit: 'Confirm these {{count}}',
      confirmExplicitTitle: 'Confirm the class of {{count}} accounts',
      confirmExplicitDescription:
        'Each account below gets the class shown next to it. Check the list before saving: some classes lower the tax.',
      confirmExplicitAction: 'Save all {{count}}',
      effect: {
        OPERATING: "Operating revenue pays PIS and COFINS under the entity's regime.",
        OPERATING_CUMULATIVE:
          'Stays at the cumulative regime rates, even in a non-cumulative entity.',
        FINANCIAL:
          "Financial revenue follows the entity's regime rate and, under the cumulative regime, is outside the base.",
        FINANCIAL_ZERO_RATED: 'Exchange variation on exports, on debts and hedges is zero-rated.',
        EQUITY_INTEREST: 'Interest on equity does not use the reduced rate of financial revenue.',
        EXPORT: 'Exports are exempt from PIS and COFINS: check that the foreign currency came in.',
        NOT_TAXED: 'Outside the PIS and COFINS base.',
      },
      suggestedTag: 'suggested',
      reviewTitle: 'Review revenue classes',
      reviewHint:
        'Changing the class of an account applies to future revenue. Provisions already posted do not change.',
      summaryOtherVersions: 'Classes are missing in other charts of accounts',
      confirmRow: 'Confirm',
      saveRow: 'Save',
      noAccounts: 'This entity has no revenue accounts yet.',
      rowClassLabel: 'Revenue class of account {{code}} {{name}}',
      columns: {
        code: 'Code',
        name: 'Account',
        class: 'Revenue class',
        action: 'Action',
      },
      result: {
        updated_one: '{{count}} account updated.',
        updated_other: '{{count}} accounts updated.',
        skipped: '{{count}} wait for your confirmation.',
        otherVersion: 'Chart {{label}}: {{count}} accounts updated.',
      },
      notPropagated: {
        title: 'Accounts in other charts of accounts that need a class',
        line: 'Account {{code}} has another name in this chart ({{targetName}}; in the current chart: {{sourceName}}). Confirm the class of this account.',
      },
      otherVersions: {
        heading: 'Chart {{version}}',
        missingLine: 'Account {{code}} {{name}} has no class yet.',
        more_one: '{{count}} more account without a class in this chart.',
        more_other: '{{count}} more accounts without a class in this chart.',
        publishedNextStep:
          'This chart of accounts is already published and its class cannot be changed from this screen. Publish a new version of the chart of accounts with the class, or ask support for help.',
        pickMonthLine:
          'This chart of accounts is already published. Choose a start from {{month}}.',
        pickMonth: 'Use {{month}}',
        versionName: '{{name}}, version {{number}}',
        summaryLine_one: '{{version}} ({{status}}): {{count}} account without a class',
        summaryLine_other: '{{version}} ({{status}}): {{count}} accounts without a class',
        statusLabels: {
          DRAFT: 'draft',
          PUBLISHED: 'published',
          SCHEDULED: 'scheduled',
          DEPRECATED: 'archived',
        },
      },
    },
    classesDialog: {
      title: 'Revenue classes',
      scope:
        'Class per account in this chart version. It applies to every entity that uses this version: {{names}}.',
      scopeOtherVersion:
        'Class per account in this chart version, which is not the active chart of {{names}}.',
      versionClean: 'Every revenue account of this chart version already has a class.',
      versionNotInUse: 'This chart version is not part of the chart of accounts of this entity.',
      saveFailed: 'The classes could not be saved.',
      retry: 'Try again',
      close: 'Close',
    },
    accounts: {
      title: 'PIS and COFINS accounts',
      intro:
        'The deduction and tax payable accounts that receive PIS and COFINS. They are created and mapped in the account mapping.',
      summaryDone: '{{count}} accounts ready',
      summaryPending: 'The PIS and COFINS accounts are not ready yet',
      openMapping: 'Open account mapping',
      notMapped: 'not mapped',
      listLabel: 'PIS and COFINS accounts',
      noChartReason: 'The entity has no active chart of accounts.',
      missingLine_one: '{{count}} account is missing. Create or map it in the account mapping.',
      missingLine_other:
        '{{count}} accounts are missing. Create or map them in the account mapping.',
    },
    start: {
      title: 'Start from',
      monthLabel: 'Start month',
      yearLabel: 'Start year',
      yearColumn: 'Year',
      monthHelp: 'Default: the current month. An earlier month is allowed.',
      backfillNote:
        'The months from {{month}} until today have no provision yet. After you start, post the provisions of those months.',
      dueBasisLabel: 'Revenue recognition',
      accrualHelp: 'The tax arises in the month of the revenue.',
      cashCondition: 'The tax arises when the customer pays.',
      cashUnavailable: 'Available only under Lucro Presumido.',
      cashCheckbox: 'The entity also uses the cash basis for IRPJ/CSLL',
      cashNeedsConfirmation: 'Confirm that the entity also uses the cash basis for IRPJ/CSLL.',
      notReadyReason: 'Finish the steps above to start.',
      lockedStep: 'Finish step {{step}} to start.',
      lockedSteps: 'Finish steps 1 and 2 to start.',
      missingPrefix: 'Missing: {{parts}}',
      missingPart: {
        classes_one: 'classify {{count}} account',
        classes_other: 'classify {{count}} accounts',
        accounts: 'create/map the PIS and COFINS accounts',
        other: 'resolve the warnings above',
      },
      save: 'Start provisioning',
    },
    started: {
      summary: 'Active since {{month}} · PIS/COFINS {{regime}} · revenue recognized on {{basis}}',
      ruleLine: '{{class}}: PIS {{pis}} · COFINS {{cofins}}',
      regime: {
        CUMULATIVE: 'cumulative',
        NON_CUMULATIVE: 'non-cumulative',
      },
      basis: {
        ACCRUAL: 'accrual',
        CASH: 'cash',
      },
      lockedPosted:
        'Provisions are already posted: the revenue recognition basis is locked and the start month cannot move later.',
      lockedNotYet:
        'Once provisions are posted, the revenue recognition basis is locked and the start month cannot move later.',
      pendingProvisions_one: '{{count}} provision has not been posted yet',
      pendingProvisions_other: '{{count}} provisions have not been posted yet',
    },
    backfill: {
      counter_one: '{{count}} entry is waiting for a provision or a provision reversal',
      counter_other: '{{count}} entries are waiting for a provision or a provision reversal',
      releasesCounter_one: '{{count}} release awaits posting',
      releasesCounter_other: '{{count}} releases await posting',
      afterFixing: 'After fixing, click the button below.',
      more: 'and {{count}} more',
      openPeriods: 'Open accounting periods',
      dialog: {
        description: 'Check what will be posted. Nothing is posted until you confirm.',
        run: 'Post',
        close: 'Close',
        cancel: 'Cancel',
        previewing: 'Checking what will be posted...',
        keepOpen:
          'Keep this window open until the end. If it is interrupted, click "Post pending provisions" again: nothing is posted twice.',
        progressStart: 'Posting…',
        progress_one: 'Posting… {{count}} entry read',
        progress_other: 'Posting… {{count}} entries read',
        progressOfTotal_one: 'Posting… {{scanned}} of {{count}} entry',
        progressOfTotal_other: 'Posting… {{scanned}} of {{count}} entries',
        consentRequired: 'Tick "I understand" under Approved guides to post.',
        cashRecovery_one:
          'This confirmation recovers {{count}} revenue source with a reconciled receipt.',
        cashRecovery_other:
          'This confirmation recovers {{count}} revenue sources with reconciled receipts.',
        cashRecoveryScopeAr: 'Batch scope: accounts receivable invoices.',
        cashRecoveryScopeContract: 'Batch scope: contract revenue.',
        cashRecoveryRemaining:
          '{{count}} sources remain. After this batch, review and confirm the next one.',
        cashRecoveryBlocked_one:
          '1 source in this batch needs its receipt, period, or origin corrected. Fix the indicated item and check again.',
        cashRecoveryBlocked_other:
          '{{count}} sources in this batch need their receipt, period, or origin corrected. Fix the indicated items and check again.',
      },
      posted: {
        preview_one: 'Will provision {{count}} revenue entry',
        preview_other: 'Will provision {{count}} revenue entries',
        previewFirst_one:
          'In the first {{scanned}} entries: will provision {{count}} revenue entry',
        previewFirst_other:
          'In the first {{scanned}} entries: will provision {{count}} revenue entries',
        previewNone: 'No new provision to post right now.',
        previewNoneFirst:
          'No provision in the first {{scanned}} entries; the run continues through the rest.',
        done: 'Provisions posted, by month',
      },
      mirrors: {
        preview_one: 'Will reverse {{count}} provision amount (one per tax and account)',
        preview_other: 'Will reverse {{count}} provision amounts (one per tax and account)',
        explain:
          'Revenue reversed after the provision month: the provisioned tax is reversed in the reversal month, for the same amount.',
        done: 'Provision reversals posted, by month',
      },
      releases: {
        preview_one: 'Will release {{count}} tax amount on receipt',
        preview_other: 'Will release {{count}} tax amounts on receipt',
        previewReversalsOnly_one: 'Will reverse {{count}} tax release',
        previewReversalsOnly_other: 'Will reverse {{count}} tax releases',
        withReversals_one: '{{title}} and {{count}} release reversal',
        withReversals_other: '{{title}} and {{count}} release reversals',
        explain:
          'When the customer pays, the tax moves out of "deferred" and becomes tax payable in the month of the receipt.',
        done: 'Releases posted, by month',
        none: 'No release to post right now.',
        pendingTitle: 'Releases still pending',
      },
      blocked: {
        title: 'Months that stay without provision',
        journals_one: '{{count}} revenue entry stays without provision',
        journals_other: '{{count}} revenue entries stay without provision',
      },
      owed: {
        title: 'Amounts in a closed period: reopen the period to post',
      },
      pending: {
        title: 'Provisions that stay pending',
        fallback: 'Setup is missing to post these provisions.',
        journals_one: '{{count}} entry',
        journals_other: '{{count}} entries',
        accounts: 'Accounts: {{accounts}}',
      },
      skipped: {
        title: 'No new provision',
        alreadyProvisioned_one: '{{count}} entry is already provisioned',
        alreadyProvisioned_other: '{{count}} entries are already provisioned',
        nettedSameMonth_one: '{{count}} entry was reversed in the same month and nets to zero',
        nettedSameMonth_other: '{{count}} entries were reversed in the same month and net to zero',
        noRevenueBase_one: '{{count}} entry has no revenue base for PIS/COFINS',
        noRevenueBase_other: '{{count}} entries have no revenue base for PIS/COFINS',
        looksPreTaxed_one:
          '{{count}} revenue entry already has PIS/COFINS in the entry itself and gets no automatic provision',
        looksPreTaxed_other:
          '{{count}} revenue entries already have PIS/COFINS in the entry itself and get no automatic provision',
        startMonthChanged_one:
          '{{count}} entry was left for the next run because the start month changed',
        startMonthChanged_other:
          '{{count}} entries were left for the next run because the start month changed',
        cancelledBeforeStartMonth_one:
          '{{count}} pending provision from a month before the start will be cancelled',
        cancelledBeforeStartMonth_other:
          '{{count}} pending provisions from months before the start will be cancelled',
        cancelledBeforeStartMonthDone_one:
          '{{count}} pending provision from a month before the start was cancelled',
        cancelledBeforeStartMonthDone_other:
          '{{count}} pending provisions from months before the start were cancelled',
        replacedByRepost_one: '{{count}} pending provision replaced by a new entry will be closed',
        replacedByRepost_other:
          '{{count}} pending provisions replaced by a new entry will be closed',
        replacedByRepostDone_one: '{{count}} pending provision replaced by a new entry was closed',
        replacedByRepostDone_other:
          '{{count}} pending provisions replaced by a new entry were closed',
      },
      failed: {
        title: 'Could not be posted',
      },
      guides: {
        title: 'Guides already approved',
        consent: 'I understand what happens to each guide above.',
        blockedPrefix: '{{month}} gets no PIS or COFINS provision: ',
        number: 'no. {{number}}',
        openGuide: 'open the guide',
        unmatched: 'unmatched balance {{amount}}',
      },
      hasMore: {
        note: 'This preview shows only the first {{scanned}} entries.',
        continues: 'The run continues until the end.',
        continuesWithTotal_one: 'The run continues until the end (about {{count}} entry).',
        continuesWithTotal_other: 'The run continues until the end (about {{count}} entries).',
      },
      setup: {
        title: 'Setup is missing before posting',
        notStarted: 'Set the PIS/COFINS provision start month before posting.',
        unavailable: 'The preview could not be built right now. Try again.',
      },
      done: {
        provisions_one: '{{count}} revenue entry provisioned',
        provisions_other: '{{count}} revenue entries provisioned',
        reversals_one: '{{count}} reversal amount posted',
        reversals_other: '{{count}} reversal amounts posted',
        cancelled_one: '{{count}} pending provision cancelled',
        cancelled_other: '{{count}} pending provisions cancelled',
        closed_one: '{{count}} pending provision closed',
        closed_other: '{{count}} pending provisions closed',
        releases_one: '{{count}} release posted',
        releases_other: '{{count}} releases posted',
        releaseReversals_one: '{{count}} release reversal posted',
        releaseReversals_other: '{{count}} release reversals posted',
        releaseFailedKept: 'The provisions of this run stayed posted.',
        and: 'and',
        summary: 'Done: {{summary}}.',
        nothing: 'Done: there was nothing new to post.',
        nothingPosted: 'Nothing was posted. See below what is still pending.',
        stopped: 'Run stopped. So far: {{summary}}.',
        stoppedNothing: 'Run stopped. Nothing was posted so far.',
        stoppedEarly:
          'What was already posted is saved and is not repeated. Click "Post pending provisions" again to continue.',
      },
      errors: {
        alreadyRunning: 'Another run is in progress. Try again in a moment.',
        guidesNeedAck:
          'Guides are already approved for these months. Read the list and tick "I understand" to continue.',
        invalidCursor:
          'Check the new preview and click Post to continue. What was already posted is not repeated.',
        invalidCursorRefreshed:
          'The preview was refreshed. What was already posted is not repeated.',
        postedSoFar: 'Already posted in this run: {{summary}}.',
        stalled:
          'The run stopped because it did not move on to the next entries. Click "Post pending provisions" again.',
        unavailable:
          'The run returned no result. Click "Post pending provisions" again: nothing is posted twice.',
      },
    },
    rules: {
      toggle: 'View rules and legal basis',
      columns: {
        class: 'Revenue class',
      },
      source: {
        DEFAULT: 'Legal default',
        TENANT_OVERRIDE: 'Organization rule',
        ENTITY_OVERRIDE: 'Entity rule',
      },
      noRule: 'No rule on the date',
      validFrom: 'Valid from {{from}}',
      validBetween: 'Valid from {{from}} to {{until}}',
      asOf: 'Rules in force on {{date}}.',
      manage: 'Manage rules',
      viewAll: 'View all rules',
      end: 'End',
      endTitle: 'End this rule?',
      endDescriptionOrganization:
        'After the chosen date, the legal default applies again to every entity in the country.',
      endDescriptionEntity:
        'After the chosen date, the organization rule or the legal default applies again.',
      endConfirm: 'End rule',
      endCancel: 'Keep',
      endDateLabel: 'The rule is valid until',
      endDateHelp: 'Inclusive. Provisions already posted do not change.',
      endDateMaxHelp:
        'Inclusive. The end can only move earlier: the rule is valid until {{until}} today. Provisions already posted do not change.',
    },
    override: {
      classLabel: 'Revenue class',
      taxLabel: 'Tax',
      outcomeLabel: 'Result',
      rateLabel: 'Rate (%)',
      rateHelp: 'Type it as a percent, for example 2.00.',
      rateEcho: '= {{percent}}',
      rateMaybe: 'Did you mean {{percent}}?',
      reasonLabel: 'Legal basis',
      reasonPlaceholder: 'E.g. court decision, case no. ...',
      save: 'Save rule',
      cancel: 'Cancel',
      errors: {
        revenueClass: 'Choose the revenue class.',
        validFrom: 'Say since when the rule is valid.',
        legalBasis: 'Give the legal basis of the rule.',
        rate: {
          EMPTY: 'Enter the rate as a percent.',
          INVALID: 'Invalid rate. Use a number, for example 2.00.',
          ZERO: 'The rate must be above zero. For zero, choose "Zero rate".',
          TOO_HIGH: 'Rate above 25 %. Check that you typed a percent.',
        },
      },
    },
    rulesPage: {
      title: 'Revenue taxes',
      subtitle:
        'PIS and COFINS on revenue, by country: the legal default and the rules of the organization or of the entities.',
      filters: {
        country: 'Country',
        entity: 'Entity',
        regime: 'Regime',
        class: 'Class',
        tax: 'Tax',
        asOf: 'Valid on',
        allCountries: 'All countries',
        allEntities: 'All entities',
        allRegimes: 'All regimes',
        allClasses: 'All classes',
        allTaxes: 'All taxes',
        toggle: 'Filters',
      },
      columns: {
        country: 'Country',
        regime: 'Regime',
        class: 'Class',
        tax: 'Tax',
        result: 'Result',
        validity: 'Validity',
        source: 'Source',
        legalBasis: 'Legal basis',
        actions: 'Actions',
      },
      scope: {
        ORGANIZATION: 'Organization',
      },
      regime: {
        CUMULATIVE: 'Cumulative',
        NON_CUMULATIVE: 'Non-cumulative',
      },
      loading: 'Loading the rules...',
      loadError: 'The rules could not be loaded. {{message}}',
      empty: {
        title: 'No rule on this date',
        description: 'Change the filters, the date in "Valid on", or create a rule.',
      },
      entityNotFound:
        'We could not find the entity of the link. Showing the rules of every entity.',
      entityNotice: {
        noRegime: 'This entity does not have a PIS/COFINS regime yet.',
        setRegime: 'Set it in Regime & Rates',
      },
      createdHint: {
        future_one: "Created, valid from {{date}} — change 'Valid on' to see it.",
        future_other:
          "{{count}} rules created, valid from {{date}} — change 'Valid on' to see them.",
        otherEntities_one: 'Created for entities outside the current filter.',
        otherEntities_other: '{{count}} rules created for entities outside the current filter.',
        filtered_one: 'Created, but the current filters do not show it.',
        filtered_other: '{{count}} rules created, but the current filters do not show them.',
      },
      count_zero: '{{count}} rules',
      count_one: '{{count}} rule',
      count_other: '{{count}} rules',
      created_one: '{{count}} rule created',
      created_other: '{{count}} rules created',
      readOnly: 'Read-only',
      newRule: 'New rule',
      endSummary: '{{class}} · {{tax}} · {{scope}}',
      dialog: {
        title: 'New rule',
        help: 'It applies from the chosen date and does not change provisions already posted. Give the legal basis.',
        countryLabel: 'Country',
        scopeLabel: 'Scope',
        scopeEntities: 'Entities',
        countryDisabledReason: 'No revenue taxes for this country yet',
        entitiesLabel: 'Entities (country: {{country}})',
        selectAll: 'Select all',
        clear: 'Clear',
        noEntities: 'No entity with country {{country}}.',
        maxEntities: 'Max. 100 entities per rule.',
        organizationHelp:
          'It applies to every entity in the country {{country}} under this regime, except those with a rule of their own.',
        regimeLabel: 'PIS/COFINS regime',
        regimePlaceholder: 'Choose the regime',
        validFromLabel: 'Valid from',
        validUntilLabel: 'Valid until',
        validUntilHelp: 'Optional. Left empty, the rule has no end date.',
        saving: 'Saving...',
        saveAndNext: 'Save and create the {{tax}} one',
        regimeMismatch:
          '{{entity}} is {{company}} ({{regime}}); this rule is for the {{ruleRegime}} regime and will not apply to it.',
        companyRegime: {
          SIMPLES_NACIONAL: 'Simples Nacional',
          LUCRO_PRESUMIDO: 'Lucro Presumido',
          LUCRO_REAL: 'Lucro Real',
        },
        errors: {
          entities: 'Choose at least one entity.',
          regime: 'Choose the regime.',
          validUntil: 'The end date cannot be before the start date.',
        },
      },
    },
  },
};
